const MFA_SESSION_ACTIVE_KEY = "mfaSessionActive";
const MFA_SESSION_ID_KEY = "mfaSessionId";
const MFA_SESSION_EMAIL_KEY = "mfaSessionEmail";
const MFA_SESSION_FIREBASE_UID_KEY = "mfaSessionFirebaseAuthUid";
const MFA_SESSION_VERIFIED_AT_KEY = "mfaSessionVerifiedAt";

// An MFA session lives in localStorage, so it survives closing the browser.
// Without an absolute cap it never expires and the user is silently let back in
// days later. Anything older than a working day requires credentials again.
const MFA_SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const isMfaSessionWithinMaxAge = () => {
  const verifiedAt = Number(localStorage.getItem(MFA_SESSION_VERIFIED_AT_KEY));

  // Sessions created before this key existed have no known age - treat them as
  // stale rather than trusting them indefinitely.
  if (!verifiedAt) return false;

  // A timestamp in the future - a clock rollback, or a value someone edited -
  // gives a negative age that would slip under the cap, so reject it too.
  const age = Date.now() - verifiedAt;

  return age >= 0 && age < MFA_SESSION_MAX_AGE_MS;
};

export const isMfaSessionActive = () => {
  if (localStorage.getItem(MFA_SESSION_ACTIVE_KEY) !== "true") return false;

  if (!isMfaSessionWithinMaxAge()) {
    clearMfaSession();
    return false;
  }

  return true;
};

// How long this session has left before it hits the cap, so a page that is
// already open can sign out on the deadline rather than at the next poll.
export const getMfaSessionRemainingMs = () => {
  if (!isMfaSessionActive()) return 0;

  const verifiedAt = Number(localStorage.getItem(MFA_SESSION_VERIFIED_AT_KEY));

  return Math.max(0, verifiedAt + MFA_SESSION_MAX_AGE_MS - Date.now());
};

export const getMfaSessionFirebaseAuthUid = () => localStorage.getItem(MFA_SESSION_FIREBASE_UID_KEY);

export const getMfaSession = () => ({
  active: isMfaSessionActive(),
  sessionId: localStorage.getItem(MFA_SESSION_ID_KEY),
  loginEmail: localStorage.getItem(MFA_SESSION_EMAIL_KEY),
  firebaseAuthUid: localStorage.getItem(MFA_SESSION_FIREBASE_UID_KEY),
});

export const setMfaSession = ({ sessionId, loginEmail, firebaseAuthUid }) => {
  localStorage.setItem(MFA_SESSION_ACTIVE_KEY, "true");
  localStorage.setItem(MFA_SESSION_ID_KEY, sessionId);
  localStorage.setItem(MFA_SESSION_EMAIL_KEY, loginEmail);
  localStorage.setItem(MFA_SESSION_FIREBASE_UID_KEY, firebaseAuthUid);
  localStorage.setItem(MFA_SESSION_VERIFIED_AT_KEY, String(Date.now()));
};

export const clearMfaSession = () => {
  localStorage.removeItem(MFA_SESSION_ACTIVE_KEY);
  localStorage.removeItem(MFA_SESSION_ID_KEY);
  localStorage.removeItem(MFA_SESSION_EMAIL_KEY);
  localStorage.removeItem(MFA_SESSION_FIREBASE_UID_KEY);
  localStorage.removeItem(MFA_SESSION_VERIFIED_AT_KEY);
};
